import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import classes from './EntityPage.module.css';

/**
 * Редактор гест-поста — екран, якого не існувало (мокап `gpEditor`).
 *
 * `generateGuestPost` кладе написане в `text_body` з першого дня, а перелік його не віддавав:
 * текст не можна було ні прочитати, ні виправити, ні скопіювати донору. При цьому аудит
 * розміщення звіряє саме цей текст із опублікованою сторінкою — тобто для системи він
 * існував, а для людини ні.
 *
 * Три речі, які екран показує поруч і які інакше ніде не зіставити: анкор, що має стояти в
 * тексті; кількість слів проти цілі; і результат аудиту з переліком перевірок, які не
 * пройшли.
 */

export interface GuestPostEditorProps {
  postId: string;
  onClose: () => void;
}

const AUDIT_LABELS: Record<string, string> = {
  FOUND_OK: 'знайдено, все гаразд',
  FOUND_TOO_SHORT: 'знайдено, замало тексту',
  FOUND_NO_ANCHOR: 'знайдено, анкора немає',
  NOT_FOUND: 'не знайдено',
};

export function GuestPostEditor({ postId, onClose }: GuestPostEditorProps): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const canWrite = useCan('linkbuilding', 'write');
  const [draft, setDraft] = useState<{
    topic?: string;
    textBody?: string;
    publishedUrl?: string;
    donorId?: string | null;
  }>({});

  const post = useQuery(trpc.links.guestPost.queryOptions({ id: postId }));
  /**
   * Донори для призначення. Реєстр — не сотні рядків (§8, «Мін. DR донора» відсіює
   * основну масу), тож один запит на весь список дешевший за пошук по мірі набору.
   */
  const donors = useQuery(trpc.links.donors.queryOptions({}));

  const save = useMutation(
    trpc.links.updateGuestPost.mutationOptions({
      onSuccess: async () => {
        // Скидаємо чернетку: поле має показувати збережене, а не набране. Сервер
        // перераховує кількість слів, і без перечитування вона лишалась би старою.
        setDraft({});
        await queryClient.invalidateQueries({ queryKey: trpc.links.pathKey() });
      },
    }),
  );

  const audit = useMutation(
    trpc.links.auditPost.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.links.pathKey() });
      },
    }),
  );

  if (post.isPending) {
    return (
      <Box className={classes.panel}>
        <Loader size="sm" />
      </Box>
    );
  }

  if (post.data === undefined) {
    return (
      <Box className={classes.panel}>
        <Text className={classes.danger} data-testid="gp-error" role="alert">
          {publicMessageOf(post.error)}
        </Text>
      </Box>
    );
  }

  const row = post.data;
  const text = draft.textBody ?? row.textBody ?? '';
  const anchorPresent = row.anchorText !== null && text.includes(row.anchorText);

  const selectedDonorId = draft.donorId !== undefined ? draft.donorId : row.donorId;
  const selectedDonor = (donors.data ?? []).find((donor) => donor.id === selectedDonorId);
  const donorBlacklisted = selectedDonor?.status === 'blacklist';

  return (
    <Box className={classes.panel} data-testid="guest-post-editor">
      <Group justify="space-between" mb="sm">
        <Group gap={8}>
          <Text className={classes.panelTitle}>{row.topic}</Text>
          <Badge size="sm" variant="default" data-testid="gp-status">
            {row.status}
          </Badge>
          {row.auditStatus === null ? null : (
            <Badge
              size="sm"
              color={row.auditStatus === 'FOUND_OK' ? 'mint' : 'rose'}
              data-testid="gp-audit"
            >
              {AUDIT_LABELS[row.auditStatus] ?? row.auditStatus}
            </Badge>
          )}
        </Group>
        <Button size="compact-xs" variant="subtle" data-testid="gp-close" onClick={onClose}>
          Закрити
        </Button>
      </Group>

      <Group gap="md" mb="sm">
        <Text className={classes.dim} data-testid="gp-donor">
          донор: {row.donorDomain ?? 'не призначено'}
        </Text>
        <Text className={classes.dim} data-testid="gp-words">
          {row.wordsActual} слів із {row.wordsTarget}
        </Text>
        {/*
         * Анкор мусить бути в тексті — інакше розміщення не дає посилання, заради якого його
         * замовляли. Це видно тут, до публікації, а не в аудиті вже опублікованої сторінки.
         */}
        {row.anchorText === null ? (
          <Text className={classes.dim} data-testid="gp-anchor-none">
            анкора не призначено
          </Text>
        ) : (
          <Text
            className={anchorPresent ? classes.dim : classes.danger}
            data-testid="gp-anchor-check"
          >
            анкор «{row.anchorText}»: {anchorPresent ? 'у тексті' : 'у тексті немає'}
          </Text>
        )}
      </Group>

      <Textarea
        // Не `autosize`: він міряє елемент через ResizeObserver, якого немає в DOM
        // компонентного тесту — те саме рішення, що в `ParsePanel` і редакторі промпту.
        rows={14}
        value={text}
        disabled={!canWrite}
        placeholder="Текст ще не згенеровано."
        data-testid="gp-text"
        onChange={(event) => {
          const { value } = event.currentTarget;
          setDraft((previous) => ({ ...previous, textBody: value }));
        }}
      />

      {/*
       * Донора можна було завести (§8, реєстр), але ніде не призначити на конкретне
       * розміщення: `donorId` лежав у `guest_posts` з першого дня, і `updateGuestPost` його
       * не приймав. Чорний список видно тут, до збереження, а не з відмови сервера постфактум.
       */}
      <Select
        size="xs"
        label="Донор"
        placeholder="не призначено"
        searchable
        clearable
        disabled={!canWrite}
        data={(donors.data ?? []).map((donor) => ({
          value: donor.id,
          label: donor.status === 'blacklist' ? `${donor.domain} · чорний список` : donor.domain,
        }))}
        value={selectedDonorId ?? null}
        data-testid="gp-donor-select"
        style={{ width: 260 }}
        onChange={(value) => {
          setDraft((previous) => ({ ...previous, donorId: value }));
        }}
      />
      {!donorBlacklisted ? null : (
        <Text className={classes.danger} mt={4} data-testid="gp-donor-blacklist" role="alert">
          Цей донор у чорному списку — розміщення на ньому не збереже посилання.
        </Text>
      )}

      <Group gap="sm" mt="sm" align="flex-end">
        <TextInput
          size="xs"
          label="Адреса розміщення"
          placeholder="https://donor.example/post/"
          value={draft.publishedUrl ?? row.publishedUrl ?? ''}
          disabled={!canWrite}
          data-testid="gp-url"
          style={{ width: 300 }}
          onChange={(event) => {
            const { value } = event.currentTarget;
            setDraft((previous) => ({ ...previous, publishedUrl: value }));
          }}
        />
        {canWrite ? (
          <>
            <Button
              size="compact-xs"
              loading={save.isPending}
              disabled={Object.keys(draft).length === 0 || donorBlacklisted}
              data-testid="gp-save"
              onClick={() => {
                save.mutate({ id: row.id, ...draft });
              }}
            >
              Зберегти
            </Button>
            {/*
             * Аудит іде читати вказану адресу. Без неї читати нічого, тож кнопка неактивна —
             * інакше вона поверталася б із «сторінку не знайдено» на порожньому рядку.
             */}
            <Button
              size="compact-xs"
              variant="default"
              loading={audit.isPending}
              disabled={(draft.publishedUrl ?? row.publishedUrl ?? '').trim() === ''}
              data-testid="gp-audit-run"
              onClick={() => {
                audit.mutate({ id: row.id });
              }}
            >
              Перевірити розміщення
            </Button>
          </>
        ) : null}
      </Group>

      {/*
       * Перелік перевірок аудиту — те, що відрізняє «не пройшло» від «не пройшло чому».
       * `checks` заповнює `auditGuestPost`, і досі його не показував жоден екран.
       */}
      {row.checks === null || typeof row.checks !== 'object' ? null : (
        <Stack gap={2} mt="sm" data-testid="gp-checks">
          {Object.entries(row.checks as Record<string, unknown>).map(([name, value]) => (
            <Text
              key={name}
              className={value === true ? classes.dim : classes.danger}
              data-testid={`gp-check-${name}`}
            >
              {value === true ? '✓' : '✕'} {name}
            </Text>
          ))}
        </Stack>
      )}

      {save.error ?? audit.error ? (
        <Text className={classes.danger} mt={8} data-testid="gp-save-error" role="alert">
          {publicMessageOf(save.error ?? audit.error)}
        </Text>
      ) : null}
    </Box>
  );
}
